import esriIdentityManager from '@arcgis/core/identity/IdentityManager';
import { LatLngBounds, Point, Projection } from 'leaflet';
import { groupBy, isNil, uniqBy } from 'ramda';

import type { DomainGuid } from '@/features/execute-calculation-set/overmind/calculation';
import type { Api } from '@/overmind/effects/api/apiFactory';

import type {
  BaseMapAdapter,
  FeatureMetaDataResponse,
  FeaturesInLayer,
  FeatureWithIDs,
  ImageSize,
  LayerId,
  LayerInfo,
  MapImage,
  SearchResult,
  SubtypeRecord
} from '../baseadapter';
import type { GetTitlesForFeatures } from './getWebMapTitles';
import { mapToInternalFeature } from './mapToInternalFeature';
import { AxiosError } from 'axios';

export type ArcgisMapServerConfig = {
  baseUrl: string;
  domainIdField: string;
  arcgisIdField: string;
  typeField: string;
  titleField: string;
  precision: number;
  searchRules: SearchRule[];
};

export type SearchRule = {
  layerId: LayerId;
  assetTypes: number[];
  attributeField: string;
};

type BoundsField = `${number},${number},${number},${number}`;

/**
 * list of numbers joined by a comma.
 * @example '1,2,3'
 */
type LayersField = string;

type FeatureSearchQueryBody = {
  where: string;
  outFields: '*';
  returnGeometry: `${boolean}`;
  geometryPrecision: `${number}`;
  outSR: '4326'; // Required by Leaflet
  f: 'geojson';
  gdbVersion?: string;
};

type ElementsInBoundsQueryBody = {
  geometry: BoundsField;
  geometryType: 'esriGeometryEnvelope';
  sr: '4326';
  layers: `all:${LayersField}`;
  mapExtent: BoundsField;
  imageDisplay: `${number},${number},${number}`; // width, height, dpi
  tolerance: `${number}`; // in pixels related to imageDisplay
  returnUnformattedValues: 'true';
  returnFieldName: 'true';
  f: 'json';
  gdbVersion?: string;
};

type ImageQueryBody = {
  bbox: BoundsField;
  bboxSR: '4326';
  imageSR: '3857'; // 4326 results in stretched images
  layers: `show:${LayersField}`;
  size: `${number},${number},${number}`;
  format: 'svg';
  transparent: `${true}`;
  f: 'json';
};

type ExportImageResponse = {
  href: `http${string}`;
  height: number;
  width: number;
  scale: number;
  extent: {
    spatialReference: {
      wkid: number;
      latestWkid: number;
    };
    xmax: number;
    xmin: number;
    ymax: number;
    ymin: number;
  };
};

type IdentifyResponseFeature = {
  layerId: number;
  layerName: string;
  displayFieldName: string;
  value: string; // shortcut for `attributes[displayFieldName]`
  attributes: GeoJSON.Feature['properties'];
  geometry: GeoJSON.Feature['geometry'];
  geometryType: string;
};

type IdentifyResponse = {
  results: IdentifyResponseFeature[];
};

export type ArcGISAdapterProps = {
  arcgisId: number;
};

type FeaturesPerLayer = Record<LayerId, FeatureWithIDs<ArcGISAdapterProps>[]>;

export type ArcGisMapAdapterOptions = {
  createApi: () => Api;
  onConfiguredToken: () => void;
  shouldUseTokenRefreshing: () => boolean;
  routeToRedirectUri: () => void;
  portalUrl: string | undefined;
  sharingUrl: string | undefined;
  restServicesUrl: string | undefined;
};

export class ArcgisMapAdapter implements BaseMapAdapter {
  private serverConfig: ArcgisMapServerConfig;
  private api: Api;

  private options: {
    onConfiguredToken: () => void;
    shouldUseTokenRefreshing: () => boolean;
    routeToRedirectUri: () => void;
    portalUrl: string | undefined;
    sharingUrl: string | undefined;
    restServicesUrl: string | undefined;
  };

  constructor(
    serverConfig: ArcgisMapServerConfig,
    options: ArcGisMapAdapterOptions
  ) {
    this.serverConfig = serverConfig;
    this.api = options.createApi();
    this.options = options;
  }

  private _registerToken(newToken: string) {
    const { portalUrl, sharingUrl, restServicesUrl } = this.options;

    if(!(portalUrl && sharingUrl && restServicesUrl)) return;

    esriIdentityManager.registerToken({
      token: newToken,
      ssl: true,
      server: portalUrl
    });
    esriIdentityManager.registerToken({
      token: newToken,
      ssl: true,
      server: sharingUrl
    });
    esriIdentityManager.registerToken({
      token: newToken,
      ssl: true,
      server: restServicesUrl
    });
  }

  async setToken(newToken: string) {
    if (this.options.portalUrl && this.options.sharingUrl && this.options.restServicesUrl) {
      const { portalUrl, sharingUrl, restServicesUrl } = this.options
      this._registerToken(newToken);

      await waitForTokenRegistrationCompletion(portalUrl, sharingUrl, restServicesUrl);
      const credential = async () => {
        const credentials = await waitForTokenRegistrationCompletion(portalUrl, sharingUrl, restServicesUrl);
        return credentials.find(credential => credential.status === 'fulfilled')
      }
      if(this.options.shouldUseTokenRefreshing()) {
        const onTokenRefreshed = (refreshedAccessToken: string) => {
          if(!refreshedAccessToken) return;
          this._registerToken(refreshedAccessToken);
          this.api.setToken(refreshedAccessToken);
        }
        if(!this.isTokenRefreshingStarted) {
          this.isTokenRefreshingStarted = true;
          startRefreshingToken({
            credential,
            onTokenRefreshed,
            routeToRedirectUri: this.options.routeToRedirectUri,
            log: {
              error: console.error,
              info: console.info
            },
            time: {
              now: Date.now,
              nowAsString: () => new Date().toISOString()
            },
            options: { minutesBeforeExpiration: 5 }
          })
        }
      }
    }

    this.api.setToken(newToken);
  }
  private isTokenRefreshingStarted = false;

  resetToken() {
    esriIdentityManager.destroyCredentials();
    this.api.setToken(undefined);
  }

  async getBackgroundMapImage(
    bounds: LatLngBounds,
    imageSize: ImageSize,
    orderedLayerIds: LayerId[],
    controller?: AbortController
  ): Promise<MapImage> {
    const requestBody = createMapImageRequestBody(
      bounds,
      imageSize,
      orderedLayerIds
    );
    const { href, extent } = await this.api.post<ExportImageResponse>(
      '/export',
      requestBody,
      controller ? { signal: controller.signal } : undefined
    );
    const imageBounds = projectFromEPSG3857(extent);

    return { bounds: imageBounds, href };
  }

  async getFeaturesInBounds(
    bounds: LatLngBounds,
    layerIdsToSearch: LayerId[],
    gridVersionName?: string,
    controller?: AbortController
  ): Promise<FeaturesInLayer[]> {
    // Here we get fetures for picker
    const requestBody = createElementsInBoundsRequestBody(
      bounds,
      layerIdsToSearch,
      gridVersionName
    );
    const response = await this.api.post<IdentifyResponse>(
      '/identify',
      requestBody,
      controller ? { signal: controller.signal } : undefined
    );
    return mapToFeaturesInLayer(layerIdsToSearch, response, this.serverConfig);
  }

  async getFeatures(
    featureIds: DomainGuid[],
    layerIdsToSearch: LayerId[],
    gridVersionName?: string,
    controller?: AbortController
  ): Promise<FeaturesInLayer[]> {
    const whereStatement = `${
      this.serverConfig.domainIdField
    } in ('${featureIds.join("','")}')`;

    const requestBody = FeatureSearchBody.create()
      .withWhereParameter(whereStatement)
      .withGdbVersionParameter(gridVersionName)
      .withGeometryPrecisionParameter(this.serverConfig)
      .build();

    return await Promise.all(
      layerIdsToSearch.map(layerId =>
        this.getFeaturesInLayer(requestBody, layerId, controller)
      )
    );
  }

  async getFeaturesWithTitle(
    featureIds: DomainGuid[],
    layerIdsToSearch: LayerId[],
    getTitlesForFeatures: GetTitlesForFeatures,
    findLayerName: (layerId: LayerId) => string | null,
    gridVersionName?: string,
    controller?: AbortController
  ): Promise<FeaturesInLayer[]> {
    const whereStatement = `${
      this.serverConfig.domainIdField
    } in ('${featureIds.join("','")}')`;

    const requestBody = FeatureSearchBody.create()
      .withWhereParameter(whereStatement)
      .withGdbVersionParameter(gridVersionName)
      .withGeometryPrecisionParameter(this.serverConfig)
      .build();

    return Promise.all(
      layerIdsToSearch.map(async layerId => {
        const layerName = findLayerName(layerId)
        return this.getFeaturesInLayerWithTitle(
          requestBody,
          layerId,
          layerName,
          getTitlesForFeatures,
          controller
        );
      })
    ).then(ensureUniqueFeaturesAcrossLayers);
  }

  async searchForFeatures(
    searchText: string,
    controller?: AbortController
  ): Promise<SearchResult[]> {
    if (!this.serverConfig.searchRules?.length) {
      return Promise.reject(new Error('NO_AVAILABLE_RULES'));
    }
    return await Promise.all(
      this.serverConfig.searchRules.map(rule => {
        const whereStatement = getSearchWhereStatement(
          searchText,
          rule,
          this.serverConfig
        );
        const requestBody = createFeatureSearchRequestBody(
          whereStatement,
          this.serverConfig
        );
        return this.searchForFeaturesInLayer(requestBody, rule, controller);
      })
    );
  }

  async getLayers(controller?: AbortController): Promise<LayerInfo[]> {
    const { layers } = await this.api.get<{ layers: LayerInfo[] }>(
      '',
      { f: 'json' },
      controller ? { signal: controller.signal } : undefined
    );
    return layers;
  }

  async getLayerDataElements(
    layerIds: number[],
    controller?: AbortController | undefined
  ): Promise<FeatureMetaDataResponse> {
    const form = new FormData();
    form.append('f', 'json');
    form.append('layers', JSON.stringify(layerIds));
    const response = await this.api.post<FeatureMetaDataResponse>(
      'queryDataElements',
      form,
      controller ? { signal: controller.signal } : undefined
    );
    return response;
  }

  async getSubtypes(
    layerIds: number[],
    controller?: AbortController | undefined
  ): Promise<SubtypeRecord> {
    const response = await this.getLayerDataElements(layerIds, controller);
    return mapToSubTypes(response);
  }

  private async getFeaturesInLayer(
    requestBody: FormData,
    layerId: LayerId,
    controller?: AbortController
  ): Promise<FeaturesInLayer<ArcGISAdapterProps>> {
    try {
      const { features } = await this.api.post<GeoJSON.FeatureCollection>(
        `/${layerId}/query`,
        requestBody,
        controller ? { signal: controller.signal } : undefined
      );
      if (features == null) {
        return { layerId, features: [], type: 'FeatureCollection' };
      }
      const featuresWithIds = features.map(feature =>
        mapToInternalFeature(
          feature,
          { type: 'LAYER_ID', layerId },
          this.serverConfig
        )
      );

      return { layerId, features: featuresWithIds, type: 'FeatureCollection' };
    } catch (error) {
      if(error instanceof AxiosError && error.response?.status === 404) {
        return { layerId, features: [], type: 'FeatureCollection' };
      }

      throw error;
    }
  }

  private async getFeaturesInLayerWithTitle(
    requestBody: FormData,
    layerId: LayerId,
    layerName: string | null,
    getTitlesForFeatures: GetTitlesForFeatures,
    controller?: AbortController
  ): Promise<FeaturesInLayer<ArcGISAdapterProps>> {
    const { features } = await this.api.post<GeoJSON.FeatureCollection>(
      `/${layerId}/query`,
      requestBody,
      controller ? { signal: controller.signal } : undefined
    )
    .catch(error => {
      return { layerId, features: [], type: 'FeatureCollection' }
    });
    if (features == null || features.length === 0) {
      return { layerId, features: [], type: 'FeatureCollection' };
    }
    const featuresWithIds = features.map(feature =>
      mapToInternalFeature(
        feature,
        layerName? { type: 'LAYER_DETAILS', layerId, layerName } : { type: 'LAYER_ID', layerId },
        this.serverConfig
      )
    );

    const webMapTitles = await getTitlesForFeatures(featuresWithIds);
    const featuresWithIdsAndTitles = featuresWithIds.map(feature => ({
      ...feature,
      gc: {
        ...feature.gc,
        webMapTitle: webMapTitles[feature.gc.globalId] === 'Unknown' || !webMapTitles[feature.gc.globalId] ? feature.gc.title : webMapTitles[feature.gc.globalId]
      }
    }));
    return {
      layerId,
      features: featuresWithIdsAndTitles,
      type: 'FeatureCollection'
    };
  }

  private async searchForFeaturesInLayer(
    requestBody: FormData,
    { layerId, attributeField }: SearchRule,
    controller?: AbortController
  ): Promise<SearchResult<ArcGISAdapterProps>> {
    const { features } = await this.api.post<GeoJSON.FeatureCollection>(
      `/${layerId}/query`,
      requestBody,
      controller ? { signal: controller.signal } : undefined
    );
    if (features == null) {
      return { attributeField, features: [] };
    }
    const featuresWithIds = features.map(feature =>
      mapToInternalFeature(
        feature,
        { type: 'LAYER_ID', layerId },
        this.serverConfig
      )
    );
    return { attributeField, features: featuresWithIds };
  }
}

function waitForTokenRegistrationCompletion(portalUrl: string, sharingUrl: string, restServicesUrl: string) {
  return Promise.allSettled([
    esriIdentityManager.checkSignInStatus(sharingUrl),
    esriIdentityManager.checkSignInStatus(restServicesUrl),
    esriIdentityManager.checkSignInStatus(portalUrl)
  ]);
}

function mapToSubTypes(response: FeatureMetaDataResponse): SubtypeRecord {
  return response.layerDataElements
    .flatMap(layer => layer.dataElement.subtypes)
    .reduce((subtypes, { subtypeCode, subtypeName }) => {
      subtypes[subtypeCode] = subtypeName;
      return subtypes;
    }, {} as SubtypeRecord);
}

function projectFromEPSG3857(
  extent: ExportImageResponse['extent']
): LatLngBounds {
  if (extent.spatialReference.latestWkid !== 3857) {
    throw new Error(
      `Expected image in wkid 3857, recieved wkid ${extent.spatialReference.latestWkid}`
    );
  }
  const southWestPoint = new Point(extent.xmin, extent.ymin);
  const northEastPoint = new Point(extent.xmax, extent.ymax);
  const southWest = Projection.SphericalMercator.unproject(southWestPoint);
  const northEast = Projection.SphericalMercator.unproject(northEastPoint);
  const imageBounds = new LatLngBounds(southWest, northEast);
  return imageBounds;
}

function ensureUniqueFeaturesAcrossLayers(features: FeaturesInLayer<unknown>[]) {
  const filteredFeatures = features.flatMap(collection => collection.features.map(f => ({ feature: f, layerId: collection.layerId })));
  const uniqueFeatures = uniqBy(f => f.feature.gc.globalId, filteredFeatures);
  const grouped = groupBy(f => f.layerId.toString(), uniqueFeatures);
  const groupedFeatures = Object.entries(grouped).map(([layerId, features]) => ({
    type: 'FeatureCollection' as const,
    layerId: Number(layerId),
    features: features.map(({ feature }) => feature)
  })) as unknown as FeaturesInLayer[];
  return groupedFeatures;
}

function mapToFeaturesInLayer(
  layerIdsToSearch: LayerId[],
  response: IdentifyResponse,
  serverConfig: ArcgisMapServerConfig
) {
  const featuresPerLayer: FeaturesPerLayer =
    layerIdsToSearch.reduce<FeaturesPerLayer>((dict, layerId) => {
      dict[layerId] = [];
      return dict;
    }, {});

  if (response?.results) {
    response.results.forEach(result => {
      const feature: FeatureWithIDs<ArcGISAdapterProps> = {
        type: 'Feature',
        properties: result.attributes,
        geometry: result.geometry,
        gc: {
          objectId: result.attributes?.[serverConfig.arcgisIdField],
          globalId: result.attributes?.[serverConfig.domainIdField],
          type: result.attributes?.[serverConfig.typeField],
          title: result.attributes?.[serverConfig.titleField],
          layerId: result.layerId,
          layerName: result.layerName
        }
      };
      if (result.layerId in featuresPerLayer) {
        featuresPerLayer[result.layerId].push(feature);
      } else {
        featuresPerLayer[result.layerId] = [feature];
      }
    });
  }

  const featuresInLayers = layerIdsToSearch.map<FeaturesInLayer>(layerId => {
    return {
      layerId,
      features: featuresPerLayer[layerId] ?? [],
      type: 'FeatureCollection'
    };
  });
  return featuresInLayers;
}

function createMapImageRequestBody(
  bounds: LatLngBounds,
  imageSize: ImageSize,
  orderedLayerIds: LayerId[]
): FormData {
  const requestBody: ImageQueryBody = {
    bbox: mapToBoundsField(bounds),
    bboxSR: '4326',
    imageSR: '3857',
    layers: `show:${orderedLayerIds.join(',')}`,
    size: `${imageSize.width},${imageSize.height},${144}`,
    format: 'svg',
    transparent: 'true',
    f: 'json'
  };
  return mapToFormData(requestBody);
}

function createElementsInBoundsRequestBody(
  bounds: LatLngBounds,
  layerIdsToSearch: LayerId[],
  gridVersionName?: string
): FormData {
  const requestBody: ElementsInBoundsQueryBody = {
    geometry: mapToBoundsField(bounds),
    geometryType: 'esriGeometryEnvelope',
    sr: '4326',
    layers: `all:${layerIdsToSearch.join(',')}`,
    mapExtent: mapToBoundsField(bounds),
    imageDisplay: `${100},${100},${96}`,
    tolerance: `${1}`,
    returnUnformattedValues: `true`,
    returnFieldName: 'true',
    f: 'json',
    ...(gridVersionName ? { gdbVersion: gridVersionName } : {})
  };
  return mapToFormData(requestBody);
}

class FeatureSearchBody {
  static create() {
    return new FeatureSearchBody();
  }

  private _geometryPrecision?: number;
  private _gdbVersion?: string;
  private _where?: string;

  withGdbVersionParameter(gdbVersionValue?: string) {
    this._gdbVersion = gdbVersionValue;
    return this;
  }

  withGeometryPrecisionParameter(serverConfig: ArcgisMapServerConfig) {
    this._geometryPrecision = serverConfig.precision;
    return this;
  }

  withWhereParameter(whereParameter: string) {
    this._where = whereParameter;
    return this;
  }

  build(): FormData {
    if (!this._where || !this._geometryPrecision) {
      throw new Error('where and geometryPrecision must be provided');
    }

    if (!this._gdbVersion) {
      const requestBody: FeatureSearchQueryBody = {
        where: this._where,
        outFields: '*',
        outSR: '4326',
        returnGeometry: 'true',
        geometryPrecision: `${this._geometryPrecision}`,
        f: 'geojson'
      };
      return mapToFormData(requestBody);
    }

    const requestBody: FeatureSearchQueryBody = {
      where: this._where,
      outFields: '*',
      outSR: '4326',
      returnGeometry: 'true',
      geometryPrecision: `${this._geometryPrecision}`,
      f: 'geojson',
      gdbVersion: this._gdbVersion
    };

    return mapToFormData(requestBody);
  }
}

function createFeatureSearchRequestBody(
  where: string,
  serverConfig: ArcgisMapServerConfig,
  gridVersionName?: string
): FormData {
  const requestBody: FeatureSearchQueryBody = {
    where,
    outFields: '*',
    outSR: '4326',
    returnGeometry: 'true',
    geometryPrecision: `${serverConfig.precision}`,
    f: 'geojson',
    ...(!isNil(gridVersionName) ? { gdbVersion: gridVersionName } : {})
  };
  return mapToFormData(requestBody);
}

function getSearchWhereStatement(
  searchText: string,
  rule: SearchRule,
  serverConfig: ArcgisMapServerConfig
): string {
  return `(${rule.attributeField} like '%${searchText}%' and ${
    serverConfig.typeField
  } in (${rule.assetTypes.join(', ')}))`;
}

function mapToBoundsField(bounds: LatLngBounds): BoundsField {
  return `${bounds.getNorthWest().lng},${bounds.getSouthEast().lat},${
    bounds.getSouthEast().lng
  },${bounds.getNorthWest().lat}`;
}

function mapToFormData(requestBody: Record<string, string>): FormData {
  const form = new FormData();
  Object.entries(requestBody).forEach(([key, value]) =>
    form.append(key, value)
  );
  return form;
}

export async function refreshTokenBeforeExpiration(context: RefreshingTokenContext) {
  return new Promise<string | undefined>(async (resolve, reject) => {
    try {
      const _credential = await context.credential();
      if(!_credential) return;

      const availableCredential = _credential as PromiseFulfilledResult<__esri.Credential>;
      if(isNil(availableCredential.value.expires)) {
        context.log.info(`No information about token expiration`)
        // any next unauthorized request (due to expired token) will be redirected to the sign in page
        // which honors defined redirect_uri (/singin-arcgis) in the app registration definition
        // we need to fallback to this route in order to force user to log in again
        context.routeToRedirectUri()
        reject(undefined)
        return
      }
      const refreshTokenTime = availableCredential.value.expires - context.time.now() - context.options.minutesBeforeExpiration * 60 * 1000
      context.log.info(`Next token referesh in ${refreshTokenTime / 60 / 1000} minutes`)
      setTimeout(() => {
        try {
          availableCredential.value.refreshToken()
          context.log.info(`Token refreshed at ${context.time.nowAsString()}`)
          resolve(availableCredential.value.token)
        } catch (error) {
          context.log.error(`Token failed refreshing at ${context.time.nowAsString()} due to: ${error}`)
          reject(undefined)
        }
      }, refreshTokenTime)
      context.log.info(`Token refreshing established at ${context.time.nowAsString()}`)
    } catch (error) {
      context.log.error(`Token failed refreshing at ${context.time.nowAsString()} due to: ${error}`)
      reject(undefined)
    }
  })
}

type RefreshingTokenContext = {
  credential: () => Promise<PromiseSettledResult<__esri.Credential> | undefined>,
  routeToRedirectUri: () => void,
  onTokenRefreshed: (token: string) => void,
  log: {
    error: (message: string) => void
    info: (message: string) => void
  }
  time: {
    now: () => number,
    nowAsString: () => string
  }
  options: { minutesBeforeExpiration: number }
}
export function startRefreshingToken(
  context: RefreshingTokenContext
) {
  refreshTokenBeforeExpiration(context)
  .then(token => {
    if(token) {
      context.onTokenRefreshed(token)
    }
    startRefreshingToken(context)
  })
}
